import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Send, Loader2, Download, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PdfDownloadGate
 * ------------------------------------------------------------------
 * Modal that appears BEFORE letting the user download a gated PDF from
 * an Insight PDF block. Collects Name / Email / WhatsApp, persists as a
 * Lead (source=`pdf-gate:{insight_slug}`) in cms_leads, then shows a
 * confirmation screen with an immediate download button.
 *
 * If RESEND_API_KEY is set on the backend, a branded email with the
 * download link is also fired (best-effort — never blocks the UX).
 *
 * Accessibility:
 *   - focus-trapped inside modal
 *   - closes on ESC / backdrop click / X button
 *   - reduced-motion friendly (fade-in only)
 */
export default function PdfDownloadGate({ open, onClose, insightSlug, blockId, fileUrl, fileTitle }) {
  const [values, setValues] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { file_url, email_sent }
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset when re-opened
    setStatus("idle"); setError(""); setResult(null); setValues({ name: "", email: "", phone: "" });
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    // Focus first input for keyboard users
    const t = setTimeout(() => firstInputRef.current?.focus(), 80);
    // Lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); clearTimeout(t); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const collectUTM = () => {
    if (typeof window === "undefined") return {};
    const q = new URLSearchParams(window.location.search);
    const out = {};
    for (const k of ["utm_source","utm_medium","utm_campaign","utm_content","utm_term","gclid","fbclid"]) {
      const v = q.get(k); if (v) out[k] = v;
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim() || !values.email.trim()) {
      setError("Preencha nome e email."); return;
    }
    setStatus("sending"); setError("");
    try {
      const { data } = await axios.post(`${API}/insights/pdf-gate`, {
        insight_slug: insightSlug,
        block_id: blockId,
        file_url: fileUrl,
        file_title: fileTitle,
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        utm: collectUTM(),
        referrer: typeof document !== "undefined" ? document.referrer : "",
      });
      setResult(data);
      setStatus("success");
      if (window.gtag) window.gtag("event", "pdf_download", { insight_slug: insightSlug });
      if (window.fbq) window.fbq("track", "Lead", { content_name: fileTitle });
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao enviar. Tente novamente.");
      setStatus("error");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-gate-title"
      data-testid="pdf-gate-modal"
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
    >
      {/* Backdrop */}
      <button
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fadein"
      />
      {/* Panel */}
      <div className="relative w-full md:max-w-lg bg-white rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden animate-slideup">
        <button
          onClick={onClose}
          data-testid="pdf-gate-close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-black/5 text-neutral-500 z-10"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>

        {status === "success" ? (
          <div className="p-8 md:p-10" data-testid="pdf-gate-success">
            <div className="flex items-center gap-2 mb-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-[10px] uppercase tracking-widest font-semibold">Liberado</span>
            </div>
            <h2 id="pdf-gate-title" className="font-display text-3xl md:text-4xl leading-tight text-black">
              Aqui está seu material. Aproveite.
            </h2>
            <p className="text-sm text-neutral-600 mt-3">
              Obrigado! Clique no botão abaixo para baixar agora.
            </p>
            <div className="mt-6">
              <a
                href={result?.file_url || fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pdf-gate-download"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 bg-accent-orange text-white font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-black transition-colors"
              >
                <Download className="w-4 h-4" /> Baixar agora
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-8">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-accent-orange mb-1">Material Places · PDF</div>
            <h2 id="pdf-gate-title" className="font-display text-2xl md:text-3xl leading-tight text-black">
              {fileTitle || "Baixe seu material"}
            </h2>
            <p className="text-xs md:text-sm text-neutral-500 mt-2">
              Preencha os dados abaixo e liberamos o download na hora.
            </p>

            <div className="mt-6 space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-1">Nome<span className="text-red-500 ml-0.5">*</span></label>
                <input
                  ref={firstInputRef}
                  value={values.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="pdf-gate-name"
                  required
                  placeholder="Seu nome"
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-1">Email<span className="text-red-500 ml-0.5">*</span></label>
                <input
                  type="email"
                  value={values.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="pdf-gate-email"
                  required
                  placeholder="voce@email.com"
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-neutral-600 mb-1">WhatsApp</label>
                <input
                  type="tel"
                  value={values.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  data-testid="pdf-gate-phone"
                  placeholder="(62) 99999-9999"
                  className="w-full border border-black/20 px-3 py-2.5 rounded focus:border-black outline-none text-sm"
                />
              </div>
            </div>

            {error && <div className="mt-3 text-sm text-red-500" data-testid="pdf-gate-error">{error}</div>}

            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="pdf-gate-submit"
              className="mt-6 w-full bg-black text-white font-semibold text-sm px-6 py-4 rounded-full hover:bg-accent-orange disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {status === "sending" ? "Enviando…" : "Receber o material"}
            </button>
            <p className="text-[10px] text-center text-neutral-400 mt-3">
              Ao continuar, você concorda em receber o material no email cadastrado.
            </p>
          </form>
        )}
      </div>
      <style>{`
        @keyframes fadein { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideup { from { transform: translateY(30px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .animate-fadein { animation: fadein .2s ease-out; }
        .animate-slideup { animation: slideup .28s cubic-bezier(.4,0,.2,1); }
      `}</style>
    </div>
  );
}
